import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { CostCodeMaster, EntryType, WorkTypeAdminRow } from '@/api/types'

const BLANK = {
  label: '',
  field_label: '',
  cost_code: '',
  earnings_code: '',
  entry_type: 'Hours' as EntryType,
  is_field_visible: true,
  sort_order: 999,
  notes: '',
}

/**
 * Work types — the pairing of a Sage cost code and earnings code behind one
 * plain-language label.
 *
 * The field picks a label and never sees a code. Accounting owns the pairings
 * here, and reclassifies individual lines before export on the correction
 * screen. Office-only types (prevailing wage, leave) exist for exactly that:
 * available to accounting, hidden from crews.
 */
export function WorkTypesPage() {
  const [rows, setRows] = useState<WorkTypeAdminRow[] | null>(null)
  const [costCodes, setCostCodes] = useState<CostCodeMaster[]>([])
  const [earnings, setEarnings] = useState<{ code: string; desc: string }[]>([])
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState({ ...BLANK })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [w, c, e] = await Promise.all([
        api.adminWorkTypes(),
        api.adminCostCodes(),
        api.adminEarningsCodes(),
      ])
      setRows(w)
      setCostCodes(c)
      setEarnings(e.map((x) => ({ code: x.earnings_code, desc: x.description ?? '' })))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load work types')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const { field, office } = useMemo(() => {
    const all = rows ?? []
    return {
      field: all.filter((r) => r.is_field_visible),
      office: all.filter((r) => !r.is_field_visible),
    }
  }, [rows])

  function startEdit(r: WorkTypeAdminRow) {
    setEditing(r.work_type_id)
    setForm({
      label: r.label,
      field_label: r.field_label ?? '',
      cost_code: r.cost_code,
      earnings_code: r.earnings_code,
      entry_type: r.entry_type,
      is_field_visible: r.is_field_visible,
      sort_order: r.sort_order,
      notes: r.notes ?? '',
    })
    setError(null)
    setNotice(null)
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const payload = { ...form, notes: form.notes || null }
      if (editing === 'new') await api.adminCreateWorkType(payload)
      else if (typeof editing === 'number') await api.adminUpdateWorkType(editing, payload)
      setEditing(null)
      await load()
      setNotice(`${form.label} saved.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  async function toggleVisible(r: WorkTypeAdminRow) {
    setBusy(true)
    setError(null)
    try {
      await api.adminUpdateWorkType(r.work_type_id, {
        label: r.label,
        // Carried through: this endpoint is a full replace, so omitting it
        // would blank the field name every time someone flipped visibility.
        field_label: r.field_label,
        cost_code: r.cost_code,
        earnings_code: r.earnings_code,
        entry_type: r.entry_type,
        is_field_visible: !r.is_field_visible,
        sort_order: r.sort_order,
        notes: r.notes,
      })
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  async function remove(r: WorkTypeAdminRow) {
    if (!confirm(`Delete "${r.label}"?`)) return
    setBusy(true)
    setError(null)
    try {
      await api.adminDeleteWorkType(r.work_type_id)
      await load()
      setNotice(`${r.label} deleted.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete')
    } finally {
      setBusy(false)
    }
  }

  const editor = (
    <div className="card">
      <h2 className="title">{editing === 'new' ? 'New work type' : 'Edit work type'}</h2>
      <div className="form-field">
        <label htmlFor="label">Label shown to the field</label>
        <input
          id="label"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Travel - Rider"
          maxLength={40}
        />
      </div>
      <div className="form-field">
        <label htmlFor="fieldLabel">Name the field sees (optional)</label>
        <input
          id="fieldLabel"
          value={form.field_label}
          onChange={(e) => setForm({ ...form, field_label: e.target.value })}
          placeholder="Travel"
          maxLength={30}
        />
        <p className="muted" style={{ fontSize: '0.8125rem', margin: '0.35rem 0 0' }}>
          What a supervisor calls it on the crew day screen. Leave blank to use
          the label above. Wording only — it moves no cost or earnings code.
        </p>
      </div>
      <div className="form-field">
        <label htmlFor="cost">Cost code (Job Cost)</label>
        <select
          id="cost"
          value={form.cost_code}
          onChange={(e) => setForm({ ...form, cost_code: e.target.value })}
        >
          <option value="">Select…</option>
          <optgroup label="Labor">
            {costCodes.filter((c) => c.is_labor).map((c) => (
              <option key={c.cost_code} value={c.cost_code}>
                {c.cost_code} — {c.description}
              </option>
            ))}
          </optgroup>
          <optgroup label="All other cost codes">
            {costCodes.filter((c) => !c.is_labor).map((c) => (
              <option key={c.cost_code} value={c.cost_code}>
                {c.cost_code} — {c.description}
              </option>
            ))}
          </optgroup>
        </select>
      </div>
      <div className="form-field">
        <label htmlFor="earn">Earnings code (Payroll)</label>
        <select
          id="earn"
          value={form.earnings_code}
          onChange={(e) => setForm({ ...form, earnings_code: e.target.value })}
        >
          <option value="">Select…</option>
          {earnings.map((e) => (
            <option key={e.code} value={e.code}>
              {e.code} — {e.desc}
            </option>
          ))}
        </select>
      </div>
      <div className="form-field">
        <label htmlFor="etype">Recorded as</label>
        <select
          id="etype"
          value={form.entry_type}
          onChange={(e) => setForm({ ...form, entry_type: e.target.value as EntryType })}
        >
          <option value="Hours">Hours</option>
          <option value="Amount">Dollar amount (posts with zero hours)</option>
        </select>
      </div>
      <div className="form-field">
        <label htmlFor="sort">Order in the field list</label>
        <input
          id="sort"
          type="number"
          value={form.sort_order}
          onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
          style={{ width: 120 }}
        />
      </div>
      <div className="form-field">
        <label>
          <input
            type="checkbox"
            checked={form.is_field_visible}
            onChange={(e) => setForm({ ...form, is_field_visible: e.target.checked })}
            style={{ minHeight: 'auto', marginRight: 8 }}
          />
          Available to supervisors in the field
        </label>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="btn" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-secondary" onClick={() => setEditing(null)}>
          Cancel
        </button>
      </div>
    </div>
  )

  const table = (list: WorkTypeAdminRow[], showHideAction: boolean) => (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Order</th>
            <th>Label</th>
            <th>Field name</th>
            <th>Cost code</th>
            <th>Earnings code</th>
            <th>Recorded as</th>
            <th className="num">Sage 12mo</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.work_type_id}>
              <td className="num muted">{r.sort_order}</td>
              <td><strong>{r.label}</strong></td>
              <td>
                {r.field_label
                  ? r.field_label
                  : <span className="muted">same</span>}
              </td>
              <td>
                <span className="mono">{r.cost_code}</span>
                <div className="muted" style={{ fontSize: '0.75rem' }}>
                  {r.cost_code_description}
                </div>
              </td>
              <td>
                <span className="mono">{r.earnings_code}</span>
                <div className="muted" style={{ fontSize: '0.75rem' }}>
                  {r.earnings_description}
                </div>
              </td>
              <td>
                <span className={`badge ${r.entry_type === 'Amount' ? 'badge-warn' : 'badge-info'}`}>
                  {r.entry_type === 'Amount' ? 'Dollar amount' : 'Hours'}
                </span>
              </td>
              <td className="num">
                {r.sage_usage_entries
                  ? r.sage_usage_entries.toLocaleString()
                  : <span className="muted">—</span>}
              </td>
              <td>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    className="btn btn-secondary"
                    style={{ minHeight: 32 }}
                    onClick={() => startEdit(r)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ minHeight: 32 }}
                    disabled={busy}
                    onClick={() => void toggleVisible(r)}
                  >
                    {showHideAction ? 'Hide from field' : 'Show in field'}
                  </button>
                  {r.usage_entries === 0 && (
                    <button
                      className="btn btn-secondary"
                      style={{ minHeight: 32 }}
                      disabled={busy}
                      onClick={() => void remove(r)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2>Work types</h2>
        <p className="muted">
          A work type pairs a <strong>cost code</strong> (where the cost lands in
          Job Cost) with an <strong>earnings code</strong> (how payroll pays it).
          They are two views of one fact, so they move together. The field picks
          a label and never sees a code.
        </p>
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          <strong>Sage 12mo</strong> is how often that exact pairing appears in
          real Time Track history — a pairing with no history is one nobody has
          used, which is worth a second look before showing it to crews.
        </p>
        {editing === null && (
          <button
            className="btn"
            onClick={() => {
              setForm({ ...BLANK })
              setEditing('new')
            }}
          >
            + Add work type
          </button>
        )}
      </div>

      {editing !== null && editor}

      <div className="card">
        <div className="crew-heading">
          <h2 className="title" style={{ margin: 0 }}>Available in the field</h2>
          <span className="count-pill">{field.length}</span>
        </div>
        {field.length === 0 ? (
          <div className="alert alert-warn">
            Nothing is available in the field. Crews cannot record time until at
            least one work type is shown.
          </div>
        ) : (
          table(field, true)
        )}
      </div>

      <div className="card">
        <div className="crew-heading">
          <h2 className="title" style={{ margin: 0 }}>Office only</h2>
          <span className="count-pill count-pill-empty">{office.length}</span>
        </div>
        <p className="muted">
          Accounting applies these when correcting a day before export —
          prevailing wage, leave, shop work. Crews never see them.
        </p>
        {office.length > 0 && table(office, false)}
      </div>
    </div>
  )
}
